/**
 * 展示网站主组件（重构后）
 * 统一管理路由和数据加载
 */

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { newsService } from '../../../services/newsService';
import { PlatformInfo, platformService } from '../../../services/platformService';
import { policyApi, PolicyItem } from '../../../services/policyService';
import WebsiteLayout from './components/WebsiteLayout';
import HomePage from './pages/HomePage';
import ShowcasePage from '../ShowcasePage/ShowcasePage';
import NewsPage from './pages/NewsPage';
import BusinessPage from '../BusinessPage/BusinessPage';
import PlatformPage from './pages/PlatformPage';
import DetailPage from './pages/DetailPage';
import DemandPage from '../DemandPage/DemandPage';
import DemandDetailPage from '../DemandPage/DemandDetailPage';
import InstitutionPage from './InstitutionPage';
import InstitutionDetailPage from './pages/InstitutionDetailPage';
import ShowcaseDetailPage from './pages/ShowcaseDetailPage';
import PolicyPage from './PolicyPage';
import FloatingBackButton from '../../../components/FloatingBackButton';
import { NewsItem as News } from '../../../types';
import './ShowcaseWebsite.css';

const ShowcaseWebsite: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const [searchParams] = useSearchParams();
    
    // 防止页面加载和路由切换时的布局缩放
    React.useLayoutEffect(() => {
        // 确保 body 没有 allow-responsive 类，防止媒体查询生效
        document.body.classList.remove('allow-responsive');
    }, []);

    // 路由变化时也确保布局稳定
    React.useLayoutEffect(() => {
        // 移除 allow-responsive 类，确保使用桌面布局
        document.body.classList.remove('allow-responsive');
    }, [location.pathname]);

    // 路径状态
    const [currentPath, setCurrentPath] = useState('main');
    const [pathInitialized, setPathInitialized] = useState(false);
    
    // 数据状态 - 仅保留DetailPage和PlatformPage需要的数据
    const [news, setNews] = useState<News[]>([]);
    const [policies, setPolicies] = useState<PolicyItem[]>([]);
    const [platformInfo, setPlatformInfo] = useState<PlatformInfo | null>(null);
    
    // 加载状态
    const [loading, setLoading] = useState(false);
    const [platformLoading, setPlatformLoading] = useState(false);

    // 根据URL路径确定当前页面
    useEffect(() => {
        const path = location.pathname;

        // 检查详情页
        const newsDetailMatch = path.match(/\/(show\/)?news\/(\d+)$/);
        const noticeDetailMatch = path.match(/\/(show\/)?notice\/(\d+)$/);
        const policyDetailMatch = path.match(/\/(show\/)?policy\/(\d+)$/);
        const showcaseDetailMatch = path.match(/\/(show\/)?showcase\/(\d+)$/);
        const institutionDetailMatch = path.match(/\/(show\/)?institutions\/(\d+)$/);
        
        if (newsDetailMatch) {
            setCurrentPath('news-detail');
            setPathInitialized(true);
            return;
        }
        if (noticeDetailMatch) {
            setCurrentPath('notice-detail');
            setPathInitialized(true);
            return;
        }
        if (policyDetailMatch) {
            setCurrentPath('policy-detail');
            setPathInitialized(true);
            return;
        }
        if (showcaseDetailMatch) {
            setCurrentPath('showcase-detail');
            setPathInitialized(true);
            return;
        }
        if (institutionDetailMatch) {
            setCurrentPath('institution-detail');
            setPathInitialized(true);
            return;
        }

        // 提取路径的最后一部分
        const pathSegments = path.split('/');
        const lastSegment = pathSegments[pathSegments.length - 1];

        if (lastSegment === 'news') setCurrentPath('news');
        else if (lastSegment === 'platform' || lastSegment === 'platform-intro') setCurrentPath('platform');
        else if (lastSegment === 'institutions') setCurrentPath('institutions');
        else if (lastSegment === 'showcase' || lastSegment === 'showcases') setCurrentPath('showcases');
        else if (lastSegment === 'business') setCurrentPath('business');
        else if (lastSegment === 'policy') setCurrentPath('policy');
        else if (lastSegment === 'demand') setCurrentPath('demand');
        else if (lastSegment === 'main') setCurrentPath('main');
        else setCurrentPath('main');
        
        setPathInitialized(true);
    }, [location.pathname, params.id]);

    // NewsPage组件有自己的状态管理，不需要在这里处理
    
    // 加载数据 - 仅在需要news/policy数据的页面时加载
    useEffect(() => {
        if (!pathInitialized) return;
        
        // 只在需要news或policies数据的页面时加载数据
        // 检查URL路径是否包含详情页路由
        const isDetailPage = location.pathname.match(/\/(news|notice|policy)\/\d+$/);
        const isPolicyDetailPage = location.pathname.match(/\/(show\/)?policy\/(\d+)$/);
        
        // 根据页面类型决定加载哪些数据
        if (currentPath === 'news' || (isDetailPage && !isPolicyDetailPage)) {
            // 资讯中心页面或新闻/通知详情页：只加载 news 数据
            loadNewsData();
        } else if (isPolicyDetailPage) {
            // 政策详情页：只加载 policy 数据（用于详情页显示）
            loadPolicyData();
        } else {
            // 清理不需要的数据（policy页面由PolicyPage组件自己管理）
            setNews([]);
            setPolicies([]);
        }
    }, [currentPath, pathInitialized, location.pathname]);

    // 加载平台信息 - 仅在platform页面时加载（main页面由HomePage组件自己管理）
    useEffect(() => {
        if (!pathInitialized) return;
        
        if (currentPath === 'platform') {
            loadPlatformInfo();
        } else {
            // 清理不需要的平台信息
            setPlatformInfo(null);
        }
    }, [currentPath, pathInitialized]);

    // 只加载 news 数据
    const loadNewsData = async () => {
        setLoading(true);
        try {
            const newsRes = await newsService.getList({ current: 1, size: 1000, status: '1' });
            if (newsRes.code === 200) {
                setNews(newsRes.data?.records || newsRes.data || []);
            }
            // 清理不需要的 policy 数据
            setPolicies([]);
        } catch (error) {
            console.error('加载新闻数据失败:', error);
        } finally {
            setLoading(false);
        }
    };

    // 只加载 policy 数据
    const loadPolicyData = async () => {
        setLoading(true);
        try {
            const policyRes = await policyApi.getList({ current: 1, size: 1000 });
            if (policyRes.code === 200) {
                setPolicies(policyRes.data?.records || policyRes.data || []);
            }
            // 清理不需要的 news 数据
            setNews([]);
        } catch (error) {
            console.error('加载政策数据失败:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadPlatformInfo = async () => {
        setPlatformLoading(true);
        try {
            const response = await platformService.getInfo();
            if (response.code === 200 && response.data) {
                setPlatformInfo(response.data);
            }
        } catch (error) {
            console.error('加载平台简介数据失败:', error);
        } finally {
            setPlatformLoading(false);
        }
    };

    
    // 查看详情
    const handleViewDetail = (item: any, category: string) => {
        if (category === 'work-dynamics' || category === 'industry-news') {
            navigate(`/show/news/${item.id}`);
        } else if (category === 'notices') {
            navigate(`/show/notice/${item.id}`);
        } else {
            navigate(`/show/news/${item.id}`);
        }
    };
    
    // 渲染内容
    const renderContent = () => {
        // 详情页
        const newsDetailMatch = location.pathname.match(/\/(show\/)?news\/(\d+)$/);
        const noticeDetailMatch = location.pathname.match(/\/(show\/)?notice\/(\d+)$/);
        const policyDetailMatch = location.pathname.match(/\/(show\/)?policy\/(\d+)$/);
        const showcaseDetailMatch = location.pathname.match(/\/(show\/)?showcase\/(\d+)$/);
        const institutionDetailMatch = location.pathname.match(/\/(show\/)?institutions\/(\d+)$/);
        const demandDetailMatch = location.pathname.match(/\/(show\/)?demand\/(\d+)$/);
        
        if (newsDetailMatch) {
            return <DetailPage news={news} policies={[]} loading={loading} type="news" />;
        }
        if (noticeDetailMatch) {
            return <DetailPage news={news} policies={[]} loading={loading} type="notice" />;
        }
        if (policyDetailMatch) {
            return <DetailPage news={[]} policies={policies} loading={loading} type="policy" />;
        }
        if (showcaseDetailMatch) {
            // 精品展示详情页
            return <ShowcaseDetailPage />;
        }
        if (institutionDetailMatch) {
            // 机构详情页
            return <InstitutionDetailPage />;
        }
        if (demandDetailMatch) {
            // 需求详情页
            return <DemandDetailPage />;
        }
        
        // 普通页面
        switch (currentPath) {
            case 'main':
                // HomePage组件已有自己的数据加载逻辑，不接受props
                return <HomePage />;
            case 'platform':
            case 'platform-intro':
                    return (
                    <PlatformPage
                        platformInfo={platformInfo || undefined}
                        loading={platformLoading}
                    />
                );
            case 'institutions':
                // InstitutionPage有自己的数据加载逻辑，不需要传入数据
                return <InstitutionPage />;
            case 'showcases':
            case 'showcase':
                // ShowcasePage组件已有自己的数据加载逻辑
                return <ShowcasePage />;
            case 'business':
                // BusinessPage组件已有自己的数据加载逻辑，但需要更新为API版本
                return <BusinessPage />;
            case 'news':
                // NewsPage组件接受ExtendedNews类型，需要类型转换
                return (
                    <NewsPage
                        news={news as any}
                        onViewDetail={handleViewDetail}
                    />
                );
            case 'policy':
                return <PolicyPage onViewDetail={handleViewDetail} />;
            case 'demand':
                // DemandPage组件已有自己的数据加载逻辑
                return <DemandPage />;
            default:
                // HomePage组件已有自己的数据加载逻辑，不接受props
                return <HomePage />;
        }
    };

    return (
        <WebsiteLayout currentPath={currentPath}>
                {renderContent()}
                <FloatingBackButton />
        </WebsiteLayout>
    );
};

export default ShowcaseWebsite;

